<template>
	<div>
		<div v-if="isLoading">
			<Loading />
		</div>
		<div v-else>
			<v-container fluid>
				<v-row
					justify="space-around"
					transition="slide-y-transition"
					v-if="!isSearch"
				>
					<v-col cols="2">
						<v-btn
							elevation="0"
							depressed
							color="#6200ea"
							icon
							@click.prevent="realodServices"
						>
							<v-icon>mdi-reload</v-icon>
						</v-btn>
					</v-col>
					<v-col cols="8">
						<v-dialog ref="dialog" v-model="modal" persistent>
							<template #activator="{on}">
								<div v-on="on" class="mb-2 text-center">
									{{ textCalendar }}
									{{ dateCalendar | moment("D MMM YYYY") }}
									<v-icon v-text="'mdi-calendar'" />
								</div>
							</template>
							<v-date-picker
								v-model="dateCalendar"
								scrollable
								:locale="lang"
								color="deep-purple accent-4"
								flat
							>
								<v-spacer></v-spacer>
								<v-btn
									text
									color="primary"
									@click="modal = false"
								>
									{{ $t("global.dialog.btnCancel") }}
								</v-btn>
								<v-btn
									text
									color="primary"
									@click="refreshServices"
								>
									{{ $t("global.dialog.btnToAccept") }}
								</v-btn>
							</v-date-picker>
						</v-dialog>
						<v-divider class="mx-4"></v-divider>
					</v-col>
					<v-col cols="2">
						<v-btn
							elevation="0"
							depressed
							color="#6200ea"
							icon
							@click.prevent="searchActive"
						>
							<v-icon>mdi-magnify</v-icon>
						</v-btn>
					</v-col>
				</v-row>
				<v-row
					justify="space-around"
					class="m-base"
					transition="slide-y-transition"
					v-else
				>
					<v-col cols="3">
						<v-btn
							color="#E5E3FF"
							tile
							@click.prevent="backCommand"
							elevation="0"
						>
							<v-icon>mdi-arrow-left</v-icon>
						</v-btn>
					</v-col>

					<v-col cols="9">
						<v-text-field
							v-model="idSearch"
							outlined
							clearable
							:label="
								$t('global.components.listServices.txtSearch')
							"
							dense
							class="mb-0"
							@click:clear="clearSearch"
						></v-text-field>
					</v-col>
				</v-row>
			</v-container>

			<div v-if="items.length > 0" class="services-container">
				<div
					v-for="item in items"
					:key="item.serviceNumber"
					class="service-item"
					:class="{ 'service-active': item.status === 'active' && item.trackingStatus !== 'pending' && item.trackingStatus !== 'finished' && item.trackingStatus !== 'noShow' }"
					@click.prevent="viewDetails(item.id)"
				>
					<!-- Header: Hora y tipo de viaje -->
					<div class="service-header">
						<div class="time-block">
							<span class="time-text">{{ item.pickupDatetime | moment("HH:mm") }}</span>
						</div>
						<div class="trip-type">
							<v-icon small color="#666">
								{{ item.trip === 'A2H' ? 'mdi-airplane-landing' : item.trip === 'H2A' ? 'mdi-airplane-takeoff' : 'mdi-map-marker' }}
							</v-icon>
						</div>
						<div class="service-meta">
							<span class="service-id">#{{ item.serviceNumber }}</span>
							<span class="pax-count">
								<v-icon x-small color="#999">mdi-account</v-icon>
								{{ item.reservation.adults + (item.reservation.children || 0) }}
							</span>
						</div>
					</div>

					<!-- Body: Ubicaciones -->
					<div class="service-body">
						<div class="locations-wrapper">
							<div class="location-line"></div>
							<div class="locations-content">
								<div class="location-row">
									<div class="location-dot dot-origin"></div>
									<div class="location-text">{{ item.pickup.name }}</div>
								</div>
								<div class="location-row">
									<div class="location-dot dot-destination"></div>
									<div class="location-text text-muted">{{ item.dropoff.name }}</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Footer: Vuelo y Estado -->
					<div class="service-footer">
						<div v-if="item.flightNumber" class="flight-info">
							<v-icon x-small color="#999" class="mr-1">mdi-airplane</v-icon>
							<span class="flight-number">{{ item.flightNumber }}</span>
							<span
								v-if="item.flightInfo"
								class="flight-status"
								:class="flightStatusClass(item.flightInfo)"
							>
								{{ flightStatusText(item.flightInfo) }}
							</span>
						</div>
						<div class="tracking-status" :class="getTrackingStatusClass(item)">
							{{ $t("lblTrackingStatus."+ item.trackingStatus+"") }}
						</div>
					</div>

					<!-- Progress bar para servicios activos -->
					<v-progress-linear
						v-if="item.status === 'active' && item.trackingStatus !== 'pending' && item.trackingStatus !== 'finished' && item.trackingStatus !== 'noShow'"
						indeterminate
						color="#666"
						height="2"
						class="service-progress"
					></v-progress-linear>
				</div>
			</div>
			<Empty
				v-else
				icon="checkbox-marked-outline"
				:msg="$t('global.components.listServices.txtEmptyServices')"
			/>
		</div>
	</div>
</template>

<script>
const Loading = () =>
	import(/* webpackChunkName: "Loading" */ "@/components/loading.vue");
const Empty = () =>
	import(/* webpackChunkName: "Empty" */ "@/components/empty.vue");

import { mapActions, mapState, mapMutations } from "vuex";

export default {
	name: "ListServices",
	components: { Loading, Empty },
	props: {
		status: {
			type: Array,
			required: true,
		},
		textCalendar: {
			type: String,
			required: true,
		},
		limitCalendar: {
			type: Number,
			defualt() {
				return 30;
			},
		},
		reverse: {
			type: Boolean,
			default() {
				return false;
			},
		},
		statusIcon: {
			type: Boolean,
			default() {
				return false;
			},
		},
		dateCalendarProp: {
			type: String,
			default() {
				return new Date().toISOString().substr(0, 10);
			},
		},
		value: String,
	},
	data() {
		return {
			date: this.$moment().format("YYYY-MM-DD"),
			isLoading: true,
			items: [],
			itemsBackup: [],
			modal: false,
			dateCalendar: null,
			isSearch: false,
			idSearch: null,
			applySearch: false,
			timeId: null,
		};
	},
	computed: {
		...mapState({ lang: (state) => state.lang }),
	},
	methods: {
		...mapActions("ui", {
			showProgress: "showProgress",
			hideProgress: "hideProgress",
		}),
		...mapMutations("ui", {
			openSnackbar: "openSnackbar",
		}),
		viewDetails(id) {
			// Dar un poco de tiempo para mirar efecto
			setTimeout(() => {
				this.$router.push({ name: "booking", params: { id } });
			}, 250);
		},
		refreshServices() {
			this.modal = false;
			this.$emit("input", this.dateCalendar);
			this.$refs.dialog.save(this.dateCalendar);
			this.getServices(this.dateCalendar, false);
		},
		realodServices() {
			this.showProgress();
			this.getServices(this.dateCalendar, false);
		},
		searchActive() {
			this.isSearch = true;
		},
		backCommand() {
			this.isSearch = false;
			this.idSearch = null;
			if (this.applySearch) {
				this.clearSearch();
			}
		},
		search() {
			if (this.itemsBackup.length < 1) {
				this.itemsBackup = this.items;
			}
			this.items = [];
			this.applySearch = true;
			// Crear la expresion
			const reg = new RegExp(`${parseInt(this.idSearch)}.*`);
			// buscar cualquier coincidencia
			this.itemsBackup.forEach((service) => {
				if (reg.test(service.reservationId)) {
					this.items.push(service);
				}
			});
		},
		clearSearch() {
			this.items = this.itemsBackup;
			this.itemsBackup = [];
			this.applySearch = false;
			this.timeId = null;
		},
		getServices(date, first) {
			if (first === undefined) {
				first = true;
			}

			if (!first) {
				this.showProgress();
			}
			this.$axios
				.get(`driver/assigned-transport-services/${date}`, {
					params:{status: this.status},
				})
				.then((res) => {
					this.items = this.reverse ? res.data.reverse() : res.data;
				})
				.catch(()=>{
					this.openSnackbar(this.$t("forgot.txTunexpectedError"));
				})
				.finally(() => {
					if (first) {
						this.isLoading = false;
					} else {
						setTimeout(() => {
							this.hideProgress();
						}, 500);
					}
				});
		},
		flightStatusText(flightInfo) {
			if (!flightInfo) return "";

			// Handle special statuses first
			if (flightInfo.status === "cancelled") return this.$t("flightStatus.cancelled");
			if (flightInfo.status === "landed") return this.$t("flightStatus.landed");

			const minutes = flightInfo.delayMinutes || 0;
			const threshold = flightInfo.minDelayThreshold || 15;

			if (minutes >= threshold) {
				// Delayed
				return this.$t("flightStatus.delayedMinutes", { minutes });
			} else if (minutes <= -threshold) {
				// Early
				return this.$t("flightStatus.earlyMinutes", { minutes: Math.abs(minutes) });
			} else {
				// On time (within threshold)
				return this.$t("flightStatus.on_time");
			}
		},
		flightStatusClass(flightInfo) {
			if (!flightInfo) return "";
			if (flightInfo.status === "cancelled") return "flight-cancelled";
			if (flightInfo.status === "landed") return "flight-landed";

			const minutes = flightInfo.delayMinutes || 0;
			const threshold = flightInfo.minDelayThreshold || 15;

			if (minutes >= threshold) return "flight-delayed";
			if (minutes <= -threshold) return "flight-early";
			return "flight-ontime";
		},
		getTrackingStatusClass(item) {
			const status = item.trackingStatus;

			if (status === "finished") return "status-finished";
			if (status === "noShow" || status === "canceled") return "status-" + status;

			const activeStates = ["onWayToPickup", "atPickup", "onWayToDropoff"];
			if (activeStates.includes(status)) return "status-active";

			if (status === "pending") {
				const pickupTime = this.$moment(item.pickupDatetime);
				const now = this.$moment();
				const minutesUntilPickup = pickupTime.diff(now, "minutes");

				if (minutesUntilPickup <= 45) {
					return "status-active";
				}
			}

			return "status-default";
		},
	},
	watch: {
		idSearch(newValue) {
			if (this.timeId !== null) {
				clearTimeout(this.timeId);
			}

			if (newValue.length < 1) {
				this.clearSearch();
			} else {
				this.timeId = setTimeout(() => {
					this.search();
				}, 500);
			}
		},
	},
	mounted() {
		if (this.value === null) {
			this.dateCalendar = new Date().toISOString().substr(0, 10);
			this.getServices(this.date, true);
		} else {
			this.dateCalendar = this.value;
			this.getServices(this.dateCalendar, true);
		}
	},
};
</script>

<style scoped>
.v-text-field__details {
	display: none !important;
}


/* Services Container */
.services-container {
	padding: 12px;
}

.service-item {
	background: #fff;
	border-radius: 12px;
	margin-bottom: 10px;
	overflow: hidden;
	cursor: pointer;
	transition: all 0.15s ease;
	border: 1px solid #e5e5e5;
}

.service-item:active {
	transform: scale(0.985);
	background: #fafafa;
}

.service-active {
	border-left: 3px solid #333;
}

/* Header */
.service-header {
	display: flex;
	align-items: center;
	padding: 12px 14px 8px;
	gap: 10px;
}

.time-block {
	display: flex;
	align-items: baseline;
}

.time-text {
	font-size: 22px;
	font-weight: 600;
	color: #1a1a1a;
	line-height: 1;
	letter-spacing: -0.5px;
}

.trip-type {
	padding: 4px;
}

.service-meta {
	margin-left: auto;
	display: flex;
	align-items: center;
	gap: 12px;
}

.service-id {
	font-size: 13px;
	font-weight: 500;
	color: #888;
}

.pax-count {
	display: flex;
	align-items: center;
	gap: 3px;
	font-size: 13px;
	font-weight: 500;
	color: #666;
}

/* Body */
.service-body {
	padding: 0 14px 10px;
}

.locations-wrapper {
	display: flex;
	position: relative;
}

.location-line {
	position: absolute;
	left: 4px;
	top: 8px;
	bottom: 8px;
	width: 1px;
	background: #ddd;
}

.locations-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.location-row {
	display: flex;
	align-items: flex-start;
	gap: 12px;
}

.location-dot {
	width: 9px;
	height: 9px;
	border-radius: 50%;
	flex-shrink: 0;
	margin-top: 4px;
}

.dot-origin {
	background: #1a1a1a;
}

.dot-destination {
	background: #fff;
	border: 2px solid #999;
}

.location-text {
	font-size: 13px;
	line-height: 1.4;
	color: #333;
}

.location-text.text-muted {
	color: #777;
}

/* Footer */
.service-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 14px;
	background: #f8f8f8;
	border-top: 1px solid #eee;
}

.flight-info {
	display: flex;
	align-items: center;
	font-size: 12px;
	color: #666;
}

.flight-number {
	font-weight: 500;
}

.flight-status {
	margin-left: 8px;
	font-size: 11px;
	font-weight: 500;
	padding: 2px 6px;
	border-radius: 4px;
}

.flight-ontime {
	color: #2e7d32;
	background: #e8f5e9;
}

.flight-delayed {
	color: #c62828;
	background: #ffebee;
}

.flight-early {
	color: #e65100;
	background: #fff3e0;
}

.flight-cancelled {
	color: #c62828;
	background: #ffebee;
}

.flight-landed {
	color: #2e7d32;
	background: #e8f5e9;
}

/* Tracking Status */
.tracking-status {
	font-size: 11px;
	font-weight: 500;
	padding: 3px 8px;
	border-radius: 4px;
	color: #666;
	background: #eee;
}

.status-default {
	color: #666;
	background: #f0f0f0;
}

.status-active {
	color: #1565c0;
	background: rgba(33, 150, 243, 0.12);
	position: relative;
	overflow: hidden;
}

.status-active::after {
	content: "";
	position: absolute;
	top: 0;
	left: -100%;
	width: 100%;
	height: 100%;
	background: linear-gradient(
		90deg,
		transparent,
		rgba(33, 150, 243, 0.2),
		transparent
	);
	animation: shimmer 2s infinite;
}

@keyframes shimmer {
	0% {
		left: -100%;
	}
	100% {
		left: 100%;
	}
}

.status-finished {
	color: #2e7d32;
	background: #e8f5e9;
}

.status-noShow,
.status-canceled {
	color: #c62828;
	background: #ffebee;
}

/* Progress */
.service-progress {
	margin: 0;
}
</style>
